<template>
  <div class="feedback-page">
    <div class="shell">
      <div class="shell-inner">
        <div class="canvas" :style="canvasStyle">
          <div class="breadcrumb">
            <img :src="assets.breadcrumbIcon" alt="首页" class="breadcrumb-icon" />
            <span class="breadcrumb-text">首页 &gt; 投诉入口与反馈</span>
          </div>

          <div class="header">
            <button type="button" class="back" @click="goHome">
              <img :src="assets.backIcon" alt="" class="back-icon" />
              <span class="back-text">返回首页</span>
            </button>
            <h1 class="title">投诉入口与反馈</h1>
          </div>

          <el-form ref="feedbackForm" :model="form" label-position="left" class="form-panel">
            <el-form-item label="投诉内容" class="form-row form-row-textarea">
              <el-input v-model.trim="form.content" type="textarea" resize="none" class="textarea"
                placeholder="请输入投诉内容" />
            </el-form-item>

            <el-form-item label="联系方式" class="form-row form-row-contact">
              <el-input v-model.trim="form.contact" class="input" placeholder="请输入联系方式" />
            </el-form-item>

            <el-form-item class="form-row form-row-upload">
              <div class="upload-row">
                <el-upload ref="uploader" class="upload-block" action="#" accept="image/*" :auto-upload="false"
                  :file-list="uploadFileList" :limit="6" list-type="picture-card" :multiple="true"
                  :show-file-list="true" :on-change="handleUploadChange" :on-preview="handleUploadPreview"
                  :on-remove="handleUploadRemove">
                  <div class="upload-trigger-wrap">
                    <div class="upload-trigger">
                      <span class="upload-plus" aria-hidden="true">+</span>
                    </div>
                    <span class="upload-text">上传证据图片</span>
                  </div>
                </el-upload>
              </div>
            </el-form-item>
          </el-form>

          <el-dialog :visible.sync="previewDialogVisible" append-to-body custom-class="preview-dialog" width="640px">
            <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
          </el-dialog>

          <button type="button" class="submit" @click="handleSubmit">
            {{ submitting ? "提交中..." : "提交" }}
          </button>

          <img :src="assets.divider" alt="" class="divider" />

          <h2 class="list-title">我的投诉（{{ complaintTotal }}）</h2>

          <div v-for="(item, index) in complaints" :key="item.id" class="record">
            <p :class="['record-content', index === 0 && 'record-content-first']">
              {{ item.content }}
            </p>

            <div v-if="item.images.length" class="record-images">
              <div v-for="(image, imageIndex) in item.images" :key="`${item.id}-image-${imageIndex}`"
                class="record-image" :style="{ backgroundImage: `url(${image})` }" />
            </div>

            <div class="record-meta">
              <span class="record-contact">联系方式：{{ item.contact }}</span>
              <span class="record-time">{{ item.createdAt }}</span>
            </div>

            <div class="reply-card">
              <img :src="assets.replyAvatar" alt="平台回复" class="reply-avatar" />
              <div class="reply-body">
                <div class="reply-content-wrap">
                  <p class="reply-content">
                    <span class="reply-prefix">平台回复：</span>
                    {{ item.reply.content }}
                  </p>
                </div>
                <p class="reply-time">{{ item.reply.createdAt }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isLoggedIn } from "@/util/token-util.js";
import bus from "@/util/bus.js";

const PAGE_CANVAS_WIDTH = 1920;

const createEmptyForm = () => ({
  content: "",
  contact: "",
});

function normalizeReply(item) {
  return {
    createdAt: item.reply_at || "",
    content: item.reply_content || "暂无平台回复",
  };
}

function normalizeImages(imgs) {
  if (Array.isArray(imgs)) {
    return imgs;
  }

  if (!imgs) {
    return [];
  }

  if (typeof imgs === "string") {
    try {
      const parsed = JSON.parse(imgs);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return imgs
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function normalizeComplaintItem(item) {
  return {
    id: String(item.id || Date.now()),
    content: item.content || "",
    contact: item.phone || "",
    createdAt: item.created_at || "",
    images: normalizeImages(item.imgs),
    reply: normalizeReply(item),
  };
}

function extractUploadUrl(uploadRes) {
  const candidates = [
    uploadRes && uploadRes.data,
    uploadRes && uploadRes.url,
    uploadRes && uploadRes.path,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const current = candidates[i];

    if (typeof current === "string" && current) {
      return current;
    }

    if (Array.isArray(current)) {
      const firstString = current.find(
        (item) => typeof item === "string" && item,
      );

      if (firstString) {
        return firstString;
      }
    }

    if (current && typeof current === "object") {
      const objectCandidates = [
        current.data,
        current.url,
        current.path,
        current.file,
        current.fileUrl,
        current.file_url,
        current.img,
        current.image,
        current.src,
      ];

      for (let j = 0; j < objectCandidates.length; j += 1) {
        if (typeof objectCandidates[j] === "string" && objectCandidates[j]) {
          return objectCandidates[j];
        }
      }
    }
  }

  return "";
}

export default {
  name: "FeedbackPage",
  created() {
    this.fetchComplaints();
  },
  data() {
    return {
      assets: {
        breadcrumbIcon: require("../../assets/feedback/breadcrumb-icon.svg"),
        backIcon: require("../../assets/feedback/back-icon.svg"),
        divider: require("../../assets/feedback/divider.svg"),
        replyAvatar: require("../../assets/feedback/reply-avatar.png"),
      },
      form: createEmptyForm(),
      complaintTotal: 0,
      pageScale: 1,
      previewDialogVisible: false,
      previewImageUrl: "",
      uploadFileList: [],
      uploadedImageUrls: [],
      uploadingImageCount: 0,
      submitting: false,
      complaints: [],
    };
  },
  computed: {
    canvasStyle() {
      return {
        zoom: this.pageScale,
      };
    },
  },
  mounted() {
    this.toggleViewportClass(true);
    this.updatePageScale();
    window.addEventListener("resize", this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    this.toggleViewportClass(false);
    this.releaseUploadFileUrls(this.uploadFileList);
  },
  methods: {
    toggleViewportClass(shouldAdd) {
      const action = shouldAdd ? "add" : "remove";

      document.documentElement.classList[action]("page-scale-fit");
      document.body.classList[action]("page-scale-fit");
    },
    handleWindowResize() {
      this.updatePageScale();
    },
    updatePageScale() {
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
      const nextScale = viewportWidth / PAGE_CANVAS_WIDTH;

      if (nextScale > 0 && nextScale !== this.pageScale) {
        this.pageScale = nextScale;
      }
    },
    ensureLoggedIn() {
      if (isLoggedIn()) {
        return true;
      }

      bus.$emit("openLoginDialog", "请登录后再查看或提交投诉");
      return false;
    },
    goHome() {
      this.$router.push("/");
    },
    async fetchComplaints() {
      if (!isLoggedIn()) {
        this.complaints = [];
        this.complaintTotal = 0;
        bus.$emit("openLoginDialog", "请登录后再查看投诉");
        return;
      }

      try {
        const res = await this.$api({
          url: "Feedback/myComplaint",
          method: "get",
          data: {
            page_num: 10,
          },
        });

        if (res.code === 200 && res.data && Array.isArray(res.data.list)) {
          this.complaints = res.data.list.map(normalizeComplaintItem);
          this.complaintTotal = Number(
            res.data.total || this.complaints.length,
          );
        }
      } catch (error) {
        console.log("fetchComplaints error", error);
      }
    },
    async handleUploadChange(file, fileList) {
      this.uploadFileList = this.normalizeUploadFileList(fileList).slice(0, 6);
      this.syncUploadedImageUrls();

      if (!file || !file.raw || file.uploadedUrl || file.uploading) {
        return;
      }

      try {
        await this.uploadComplaintImage(file);
      } catch (error) {
        this.$message.error("图片上传失败，请重新选择");
        this.removeUploadFileByUid(file.uid);
      }
    },
    handleUploadRemove(file, fileList) {
      this.releaseUploadFileUrls([file]);
      this.uploadFileList = this.normalizeUploadFileList(fileList);
      this.syncUploadedImageUrls();
    },
    handleUploadPreview(file) {
      this.previewImageUrl = file.url || file.uploadedUrl || "";
      this.previewDialogVisible = Boolean(this.previewImageUrl);
    },
    normalizeUploadFileList(fileList) {
      return fileList.map((item) => {
        if (!item.url && item.raw) {
          item.url = URL.createObjectURL(item.raw);
        }

        return item;
      });
    },
    releaseUploadFileUrls(fileList) {
      fileList.forEach((item) => {
        if (item && item.url && item.url.indexOf("blob:") === 0) {
          URL.revokeObjectURL(item.url);
        }
      });
    },
    syncUploadedImageUrls() {
      this.uploadedImageUrls = this.uploadFileList
        .map((item) => item && item.uploadedUrl)
        .filter((item) => typeof item === "string" && item);
    },
    findUploadFileByUid(uid) {
      return (
        this.uploadFileList.find((item) => item && item.uid === uid) || null
      );
    },
    removeUploadFileByUid(uid) {
      const nextFileList = this.uploadFileList.filter(
        (item) => !item || item.uid !== uid,
      );
      const removedFile = this.findUploadFileByUid(uid);

      if (removedFile) {
        this.releaseUploadFileUrls([removedFile]);
      }

      this.uploadFileList = nextFileList;
      this.syncUploadedImageUrls();

      if (this.$refs.uploader && this.$refs.uploader.uploadFiles) {
        this.$refs.uploader.uploadFiles = nextFileList;
      }
    },
    clearUploadFiles() {
      this.releaseUploadFileUrls(this.uploadFileList);
      this.uploadFileList = [];
      this.uploadedImageUrls = [];

      if (this.$refs.uploader && this.$refs.uploader.clearFiles) {
        this.$refs.uploader.clearFiles();
      }
    },
    async uploadComplaintImage(file) {
      const targetFile = this.findUploadFileByUid(file.uid) || file;

      if (!targetFile || !targetFile.raw) {
        return "";
      }

      if (targetFile.uploadedUrl) {
        return targetFile.uploadedUrl;
      }

      const formData = new FormData();
      formData.append("fname", targetFile.raw);

      this.$set(targetFile, "uploading", true);
      this.uploadingImageCount += 1;

      try {
        const uploadRes = await this.$uploadApi(formData, "Common/uploadFile");
        const uploadedUrl = extractUploadUrl(uploadRes);

        if (!uploadedUrl) {
          throw new Error("empty uploaded url");
        }

        this.$set(targetFile, "uploadedUrl", uploadedUrl);
        this.syncUploadedImageUrls();
        return uploadedUrl;
      } finally {
        this.$set(targetFile, "uploading", false);
        this.uploadingImageCount = Math.max(0, this.uploadingImageCount - 1);
      }
    },
    async handleSubmit() {
      if (!this.form.content || !this.form.contact) {
        this.$message.warning("请先完整填写投诉内容和联系方式");
        return;
      }

      if (!/^1[3-9]\d{9}$/.test(this.form.contact)) {
        this.$message.warning("请输入正确的手机号码");
        return;
      }

      if (!this.ensureLoggedIn()) {
        return;
      }

      if (this.uploadingImageCount > 0) {
        this.$message.warning("图片正在上传中，请稍后再提交");
        return;
      }

      this.submitting = true;

      try {
        const res = await this.$api({
          url: "Feedback/complaint",
          method: "post",
          data: {
            content: this.form.content,
            phone: this.form.contact,
            imgs: this.uploadedImageUrls.join("|"),
          },
        });

        if (res.code === 200) {
          this.$message.success(res.msg || "投诉提交成功");
          this.form = createEmptyForm();
          this.clearUploadFiles();
          this.fetchComplaints();
          return;
        }

        this.$message.error(res.msg || "投诉提交失败");
      } catch (error) {
        this.$message.error("投诉提交失败，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.feedback-page {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: #ffffff;
}

.shell {
  width: 100%;
}

.shell-inner {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.canvas {
  flex: 0 0 auto;
  width: 1920px;
  min-height: 1617px;
  padding-bottom: 157px;
  overflow: hidden;
  transform-origin: top left;
}

.breadcrumb {
  display: flex;
  align-items: center;
  align-self: stretch;
  min-width: 1920px;
  height: 40px;
  padding: 10px 0 10px 11px;
  background: #f7f7f7;
}

.breadcrumb-icon {
  width: 14px;
  height: 13px;
}

.breadcrumb-text {
  margin-left: 10px;
  color: #666666;
  font-size: 14px;
  line-height: 20px;
}

.header {
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  margin-top: 19px;
  padding-left: 35px;
}

.back {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d92f24;
  border-radius: 4px;
  width: 118px;
  min-width: 118px;
  height: 43px;
  padding: 10px 14px 9px 13px;
  background: #ffffff;
  cursor: pointer;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.back-text {
  color: #d92f24;
  font-size: 16px;
  line-height: 22px;
}

.title {
  margin: 11px 0 0 716px;
  color: #000000;
  font-size: 36px;
  font-weight: 700;
  line-height: 43px;
}

.form-panel {
  width: 1191px;
  margin: 47px 366px 0 363px;
  overflow: hidden;
}

.form-row {
  margin-bottom: 0;
}

.form-row /deep/ .el-form-item__label {
  width: 82px;
  padding: 10px 0 0;
  color: #000000;
  font-size: 16px;
  line-height: 22px;
}

.form-row /deep/ .el-form-item__content {
  margin-left: 82px !important;
}

.form-row-contact {
  margin-top: 25px;
}

.form-row-upload {
  margin-top: 32px;
}

.form-row-textarea /deep/ .el-form-item__label {
  padding-top: 12px;
}

.input /deep/ .inner,
.textarea /deep/ .inner {
  box-sizing: border-box;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  color: #000000;
  font-size: 16px;
  line-height: 22px;
}

.input /deep/ .inner {
  width: 1050px;
  height: 44px;
  padding: 10px 22px;
}

.textarea /deep/ .inner {
  width: 1050px;
  height: 136px;
  padding: 11px 22px;
  resize: none;
}

.input /deep/ .inner::placeholder,
.textarea /deep/ .inner::placeholder {
  color: #666666;
}

.upload-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 22px;
}

.upload-block {
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
}

.upload-block /deep/ .el-upload-list--picture-card {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  order: -1;
  margin: 0 20px 0 0;
  padding: 0;
}

.upload-block /deep/ .item {
  margin: 0;
  border: 1px solid #979797;
  border-radius: 8px;
  width: 120px;
  height: 120px;
  overflow: hidden;
}

.upload-block /deep/ .item-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #ffffff;
}

.upload-block /deep/ .item-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-block /deep/ .el-upload--picture-card {
  border: 0;
  width: auto;
  height: auto;
  margin: 0;
  background: transparent;
}

.upload-trigger-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.upload-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  width: 60px;
  height: 60px;
  padding: 0;
  background: #ffffff;
  cursor: pointer;
}

.upload-plus {
  color: #666666;
  font-size: 32px;
  line-height: 1;
  font-weight: 300;
}

.upload-text {
  margin-top: 9px;
  color: #666666;
  font-size: 16px;
  line-height: 22px;
}

.upload-block /deep/ .el-upload {
  display: flex;
  align-items: flex-start;
}

.preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  object-fit: contain;
}

.submit {
  margin-left: 445px;
  border: 0;
  border-radius: 8px;
  width: 131px;
  height: 44px;
  background: #d92f24;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.divider {
  width: 1922px;
  height: 1px;
  margin-top: 40px;
  margin-left: -1px;
}

.list-title {
  margin: 31px 0 0 260px;
  color: #333333;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
}

.record-content {
  margin: 47px 0 0 260px;
  max-width: 1120px;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
}

.record-content-first {
  margin-top: 30px;
}

.record-images {
  display: flex;
  gap: 20px;
  margin-top: 22px;
  margin-left: 260px;
}

.record-image {
  border: 1px solid #979797;
  border-radius: 8px;
  width: 120px;
  height: 120px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.record-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 375px;
  margin-top: 22px;
  margin-left: 261px;
}

.record-contact,
.record-time {
  color: #333333;
  font-size: 16px;
  line-height: 22px;
}

.reply-card {
  display: flex;
  align-items: flex-start;
  margin: 22px 260px 0;
  border-radius: 8px;
  background: #f1f1f1;
  padding: 19px 22px 19px 21px;
}

.reply-time {
  margin: 18px 0 0;
  color: #666666;
  font-size: 14px;
  line-height: 20px;
}

.reply-avatar {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
}

.reply-body {
  flex: 1;
  min-width: 0;
  margin-left: 21px;
}

.reply-content {
  margin: 0;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
  word-break: break-all;
  text-indent: 2em;
}

.reply-prefix {
  font-weight: 700;
}
</style>
<style scoped src="./index.mobile.css"></style>
